import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProperty, PropertyProvider } from '../contexts/PropertyContext';
import { propertiesAPI, bookingsAPI } from '../lib/api';
import { format } from 'date-fns';
import {
  LayoutDashboard,
  CalendarDays,
  BedDouble,
  CalendarCheck,
  Receipt,
  ClipboardList,
  Building2,
  Users,
  LogOut,
  ChevronDown,
  CalendarRange,
  Sparkles,
  Menu,
  X,
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/diary', label: 'Diary', icon: CalendarDays },
  { path: '/admin/bookings', label: 'Bookings', icon: CalendarCheck },
  { path: '/admin/ledger', label: 'Payments', icon: Receipt },
  { path: '/admin/housekeeping', label: 'Housekeeping', icon: Sparkles },
  { path: '/admin/calendar', label: 'Availability', icon: CalendarRange },
  { path: '/admin/guests', label: 'Guests', icon: ClipboardList },
  { path: '/admin/rooms', label: 'Rooms', icon: BedDouble },
  { path: '/admin/properties', label: 'Properties', icon: Building2, roles: ['superadmin'] },
  { path: '/admin/users', label: 'Users', icon: Users, roles: ['superadmin'] },
];

function AdminLayoutInner({ children }) {
  const { user, logout } = useAuth();
  const { selectedProperty, setSelectedProperty } = useProperty();
  const location = useLocation();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [propDropdownOpen, setPropDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dailyMetrics, setDailyMetrics] = useState(null);
  const propDropdownRef = useRef(null);

  useEffect(() => {
    propertiesAPI.list().then(res => setProperties(res.data || [])).catch(() => {});
    const today = format(new Date(), 'yyyy-MM-dd');
    bookingsAPI.daily(today).then(res => setDailyMetrics(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (propDropdownRef.current && !propDropdownRef.current.contains(e.target)) setPropDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const navItems = NAV_ITEMS.filter((item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  });

  const isActive = useCallback((item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/admin/login'); };
  const handlePropertyChange = (id) => { setSelectedProperty(id); setPropDropdownOpen(false); };

  const selectedPropName = selectedProperty
    ? (properties.find(p => p.id === selectedProperty)?.name || 'Property')
    : 'All Properties';

  return (
    <div className="min-h-screen bg-[#F4F5F9]" data-testid="admin-layout">
      {/* ======== HEADER ======== */}
      <header className="bg-white sticky top-0 z-40 border-b border-[#EAECF0]" data-testid="admin-topbar">
        {/* Row 1: Logo + Controls */}
        <div className="flex items-center h-14 px-4 lg:px-6 border-b border-gray-100">
          {/* Logo */}
          <Link to="/admin" className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/></svg>
            </div>
            <span className="text-[15px] font-bold text-gray-900 tracking-tight hidden sm:block">Resident Hotels</span>
          </Link>

          {/* Right side */}
          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            {/* Metrics pills */}
            <div className="hidden md:flex items-center gap-2">
              {[
                { label: 'IN', val: `${dailyMetrics?.arrivals_done || 0}/${dailyMetrics?.arrivals_total || 0}`, color: 'text-emerald-600 bg-emerald-50' },
                { label: 'OUT', val: `${dailyMetrics?.departures_done || 0}/${dailyMetrics?.departures_total || 0}`, color: 'text-blue-600 bg-blue-50' },
                { label: 'STAY', val: `${dailyMetrics?.stayovers_total || 0}`, color: 'text-amber-600 bg-amber-50' },
              ].map(m => (
                <span key={m.label} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold ${m.color}`}>
                  {m.label} <span className="font-bold">{m.val}</span>
                </span>
              ))}
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px h-6 bg-gray-200" />

            {/* Property Selector */}
            <div className="relative" ref={propDropdownRef}>
              <button
                onClick={() => setPropDropdownOpen(!propDropdownOpen)}
                className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-gray-200 hover:border-gray-300 bg-white text-[13px] font-medium text-gray-700 transition-colors"
                data-testid="property-selector"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                <span className="max-w-[120px] truncate">{selectedPropName}</span>
                <ChevronDown className="h-3 w-3 text-gray-400" />
              </button>
              {propDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[200px] py-1">
                  <button
                    onClick={() => handlePropertyChange('')}
                    className={`w-full text-left px-3 py-2 text-[13px] transition-colors ${!selectedProperty ? 'text-violet-700 bg-violet-50 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                    data-testid="prop-option-all"
                  >
                    All Properties
                  </button>
                  {properties.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handlePropertyChange(p.id)}
                      className={`w-full text-left px-3 py-2 text-[13px] transition-colors ${selectedProperty === p.id ? 'text-violet-700 bg-violet-50 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                      data-testid={`prop-option-${p.id}`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User */}
            <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 text-xs font-bold flex-shrink-0">
              {(user?.name || 'A')[0].toUpperCase()}
            </div>

            {/* Logout */}
            <button onClick={handleLogout} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Logout" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </button>

            {/* Mobile menu toggle */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-1.5 rounded-md hover:bg-gray-100" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="h-5 w-5 text-gray-600" /> : <Menu className="h-5 w-5 text-gray-600" />}
            </button>
          </div>
        </div>

        {/* Row 2: Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1 px-4 lg:px-6 h-11 overflow-x-auto" data-testid="admin-nav">
          {navItems.map((item) => {
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-violet-50 text-violet-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className={`h-3.5 w-3.5 ${active ? 'text-violet-600' : 'text-gray-400'}`} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-gray-100 bg-white px-4 py-3 grid grid-cols-2 gap-1" data-testid="admin-nav-mobile">
            {navItems.map((item) => {
              const active = isActive(item);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-[13px] font-medium ${
                    active ? 'bg-violet-50 text-violet-700' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className={`h-4 w-4 ${active ? 'text-violet-600' : 'text-gray-400'}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      {/* ======== MAIN CONTENT ======== */}
      <main className="px-4 lg:px-6 py-6 max-w-[1400px] mx-auto" data-testid="admin-dashboard">
        {children}
      </main>
    </div>
  );
}

export const AdminLayout = ({ children }) => {
  return (
    <PropertyProvider>
      <AdminLayoutInner>{children}</AdminLayoutInner>
    </PropertyProvider>
  );
};
